// ═══════════════════════════════════════════════════════════════════════
// Temps › Qualité du paramétrage (RH) — ce qui empêche d'interpréter correctement les journées :
// salariés actifs sans horaire, sans responsable N+1 habilité, paramètres à valider, manquants ou à
// confirmer, jours fériés de l'année non validés. Un lien par indicateur vers l'écran qui corrige.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Badge, BandeauInfo, Squelette, Selecteur } from '../../design/composants';
import { NavTemps, useDroitsTemps, AccesTemps, lireRh, messageErreur, nomPersonne, jourCourt } from './rhCommun';

const ICONES = { succes: CheckCircle2, danger: XCircle, alerte: AlertTriangle, neutre: Info };
const COULEURS = { succes: 'var(--sg-succes)', danger: 'var(--sg-danger)', alerte: 'var(--sg-alerte)', neutre: 'var(--sg-texte-3)' };

/** Une ligne d'élément, selon l'indicateur. */
function texteElement(code, e) {
  if (code === 'SANS_HORAIRE') return `${nomPersonne(e)} · ${e.matricule}${e.site_nom ? ` · ${e.site_nom}` : ''}`;
  if (code === 'SANS_N1') return `${nomPersonne(e)} · ${e.matricule} — ${e.raison === 'AUCUN_N1' ? 'aucun responsable défini' : `${nomPersonne(e.responsable)} n'a pas le droit de valider`}`;
  if (code === 'FERIES_NON_VALIDES') return `${jourCourt(e.date)} · ${e.libelle}${e.site_nom ? ` (${e.site_nom})` : ''}`;
  return `${e.libelle}${e.valeur ? ` : ${e.valeur}` : ''}${e.date_debut ? ` (à partir du ${e.date_debut})` : ''}${e.auteur ? ` · proposé par ${e.auteur}` : ''}`;
}

function Indicateur({ ind }) {
  const [tout, setTout] = useState(false);
  const Icone = ICONES[ind.ton] || Info;
  const visibles = tout ? ind.elements : ind.elements.slice(0, 8);
  return (
    <Carte titre={ind.libelle} Icone={Icone}
      action={(
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <Badge ton={ind.ton}>{ind.nombre}</Badge>
          {ind.lien && ind.nombre > 0 && <Link to={ind.lien} className="rh-btn rh-btn-petit rh-btn-discret" style={{ textDecoration: 'none' }}>Corriger<ArrowRight size={14} aria-hidden="true" /></Link>}
        </span>
      )}>
      <p className="rh-form-aide" style={{ margin: '0 0 6px', display: 'flex', gap: 6 }}><Icone size={14} aria-hidden="true" style={{ color: COULEURS[ind.ton], flexShrink: 0, marginTop: 2 }} />{ind.aide}</p>
      {ind.nombre === 0 ? <p style={{ margin: 0, fontSize: 13, color: 'var(--sg-succes)' }}>Rien à signaler.</p> : (
        <>
          <ul className="rh-liste">{visibles.map((e) => <li key={e.id || e.code}>{texteElement(ind.code, e)}</li>)}</ul>
          {ind.elements.length > 8 && <button type="button" className="rh-btn rh-btn-lien rh-btn-petit" onClick={() => setTout(!tout)}>{tout ? 'Voir moins' : `Voir les ${ind.elements.length}`}</button>}
        </>
      )}
    </Carte>
  );
}

export default function RhQualitePage() {
  const droits = useDroitsTemps();
  const [site, setSite] = useState('');
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const charger = useCallback(async () => {
    setErreur(''); setDonnees(null);
    try { setDonnees(await lireRh('/qualite', { site: site || undefined })); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [site]);
  useEffect(() => { if (droits?.rh?.menu?.qualite) charger(); }, [droits, charger]);
  const aRegler = donnees ? donnees.indicateurs.filter((i) => i.nombre > 0 && i.ton !== 'neutre') : [];

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Qualité du paramétrage' }]} titre="Qualité du paramétrage"
          sousTitre="Ce qui empêche le calcul correct des journées : à corriger avant la clôture du mois." />
        <NavTemps actif="qualite" />
        <AccesTemps droits={droits} rubrique="qualite">
          {droits?.rh?.sites?.length > 1 && (
            <div className="rh-outils"><div style={{ minWidth: 220 }}><Selecteur libelle="Site" valeur={site} onChange={setSite} options={droits.rh.sites.map((s) => ({ valeur: s.id, libelle: s.nom }))} /></div></div>
          )}
          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
          {!donnees && !erreur && <Squelette largeur="60%" />}
          {donnees && (
            <>
              <BandeauInfo ton={aRegler.length ? 'alerte' : 'succes'} titre={aRegler.length ? `${aRegler.length} point${aRegler.length > 1 ? 's' : ''} à corriger` : 'Paramétrage complet'}>
                {donnees.effectif} salarié{donnees.effectif > 1 ? 's' : ''} actif{donnees.effectif > 1 ? 's' : ''} dans votre périmètre, au {jourCourt(donnees.date)}.
              </BandeauInfo>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
                {donnees.indicateurs.map((i) => <Indicateur key={i.code} ind={i} />)}
              </div>
            </>
          )}
        </AccesTemps>
      </div>
    </Layout>
  );
}
